import { useAtom } from 'jotai';
import { RESET } from 'jotai/utils';
import { useEffect, useState } from 'react';
import type {
  DropDownListViewModel,
  DropDownRepositoryResponse,
  QuestionLibraryItemRepositoryResponse,
} from '@/shared/api/generated';
import {
  useGetApiV1ExamAuthorDropdown,
  useGetApiV1ExamCompanyDropdown,
  useGetApiV1ExamComponentTypes,
  useGetApiV1ExamDisciplineDropdownFiltered,
  useGetApiV1ExamQuestionLibrary,
} from '@/shared/api/generated';

import { type QuestionFilters, questionFiltersAtom } from '../store/atoms/questionFiltersAtom';

// ─── Tipos de domínio ─────────────────────────────────────────────────────────

export type QuestionStatus = 'Aprovado' | 'Pendente' | 'Rejeitado' | 'EmRevisao';
export type QuestionLanguage = 'pt-BR' | 'en-US' | 'es-ES';
export type QuestionType = string;

/** Entidade Question — modelo de domínio da feature, mapeado da API */
export interface Question {
  readonly id: string;
  readonly text: string;
  readonly discipline: string;
  readonly author: string;
  readonly language: QuestionLanguage;
  readonly status: QuestionStatus;
  readonly isActive: boolean;
  readonly type: QuestionType;
  readonly isOwner: boolean;
  /** ID da empresa (string para compatibilidade com formulário de edição). Null quando não retornado pelo endpoint de listagem. */
  readonly company: string | null;
  /** Data de criação. Null quando não retornada pelo endpoint de listagem. */
  readonly createdAt: string | null;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Mapeia o enum `EExamItemStatus` do backend para o status de domínio da feature.
 * `1` = Pending, `2` = Approved, `3` = Rejected.
 */
export const STATUS_MAP: Record<number, QuestionStatus> = {
  1: 'Pendente',
  2: 'Aprovado',
  3: 'Rejeitado',
};

function mapToQuestion(item: QuestionLibraryItemRepositoryResponse): Question {
  return {
    id: String(item.id ?? ''),
    text: item.label ?? '',
    discipline: item.disciplineName ?? '',
    author: item.personAuthorName ?? '',
    language: (item.cultureInfo ?? 'pt-BR') as QuestionLanguage,
    status: STATUS_MAP[item.status ?? 0] ?? 'Pendente',
    isActive: item.active ?? false,
    type: item.componentType ?? '',
    isOwner: item.isOwner ?? false,
    company: null,
    createdAt: null,
  };
}

const PAGE_SIZE = 20;

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Hook de gerenciamento da Biblioteca de Questões.
 *
 * Encapsula filtros (via Jotai), paginação server-side e dados reais da API.
 * Usa os hooks gerados pelo Kubb: `useGetApiV1ExamQuestionLibrary`,
 * `useGetApiV1ExamAuthorDropdown`, `useGetApiV1ExamCompanyDropdown` e
 * `useGetApiV1ExamComponentTypes`.
 *
 * @returns Dados paginados, estado de loading, filtros e callbacks de controle.
 *
 * @example
 * ```tsx
 * const { data, isLoading, filters, updateFilters, resetFilters } = useQuestionLibrary();
 * ```
 */
export function useQuestionLibrary() {
  const [filters, setFilters] = useAtom(questionFiltersAtom);

  const [disciplineSearch, setDisciplineSearch] = useState('');
  const [debouncedDisciplineFilter, setDebouncedDisciplineFilter] = useState('');

  useEffect(() => {
    if (disciplineSearch === '') {
      setDebouncedDisciplineFilter('');
      return;
    }
    const timer = setTimeout(() => setDebouncedDisciplineFilter(disciplineSearch), 1000);
    return () => clearTimeout(timer);
  }, [disciplineSearch]);

  const isDisciplineReady = debouncedDisciplineFilter.length >= 3;

  const queryParams = {
    PageNumber: filters.page,
    PageSize: PAGE_SIZE,
    ...(filters.disciplineId !== null ? { DisciplineId: filters.disciplineId } : {}),
    ...(filters.personId !== null ? { PersonId: filters.personId } : {}),
    ...(filters.companyId !== null ? { CompanyId: filters.companyId } : {}),
    ...(filters.componentType !== null ? { ComponentType: filters.componentType } : {}),
    ...(filters.cultureInfo ? { CulturesInfo: filters.cultureInfo } : {}),
    ...(filters.startDate ? { StartDate: filters.startDate } : {}),
    ...(filters.endDate ? { EndDate: filters.endDate } : {}),
    ...(filters.labelSearch ? { LabelSearch: filters.labelSearch } : {}),
  };
  const query = useGetApiV1ExamQuestionLibrary(queryParams);

  const authorsQuery = useGetApiV1ExamAuthorDropdown();
  const companiesQuery = useGetApiV1ExamCompanyDropdown();
  const componentTypesQuery = useGetApiV1ExamComponentTypes();
  const disciplinesQuery = useGetApiV1ExamDisciplineDropdownFiltered(
    isDisciplineReady ? { filter: debouncedDisciplineFilter } : undefined,
    { query: { enabled: isDisciplineReady } },
  );

  const rawData = query.data?.data ?? [];
  const data: Question[] = rawData.map(mapToQuestion);

  const totalCount = query.data?.totalCount ?? 0;
  const totalPages = query.data?.totalPages ?? 1;
  const currentPage = query.data?.currentPage ?? filters.page;

  const authorOptions: DropDownRepositoryResponse[] = authorsQuery.data ?? [];
  const companyOptions: DropDownRepositoryResponse[] = companiesQuery.data ?? [];
  const componentTypeOptions: DropDownListViewModel[] = componentTypesQuery.data ?? [];
  const disciplineOptions: DropDownRepositoryResponse[] = disciplinesQuery.data ?? [];

  function updateFilters(partial: Partial<QuestionFilters>) {
    setFilters(prev => ({
      ...prev,
      ...partial,
      page: 'page' in partial ? (partial.page ?? 1) : 1,
    }));
  }

  function resetFilters() {
    setFilters(RESET);
  }

  return {
    data,
    isLoading: query.isPending,
    isError: query.isError,
    totalCount,
    currentPage,
    totalPages,
    filters,
    authorOptions,
    companyOptions,
    componentTypeOptions,
    disciplineOptions,
    isDisciplineQuerySuccess: disciplinesQuery.isSuccess,
    updateFilters,
    resetFilters,
    setDisciplineSearch,
  } as const;
}
